import { Control, useWatch } from 'react-hook-form';
import { CreateEstimateFormData } from '@/types/estimates/CreateEstimateFormData';
import { YesNoStyledRadioGroup } from '../styled/StyledRadioGroup';
import { Label } from '../ui/label';
import { useTranslation } from '@/i18n';

interface FlexibilityRowProps {
  control: Control<CreateEstimateFormData>;
  name: keyof CreateEstimateFormData;
  label: string;
  indented?: boolean;
}

interface EstimateAgencyFlexibilityConfigProps {
  control: Control<CreateEstimateFormData>;
}

const EstimateAgencyFlexibilityConfig = ({
  control,
}: EstimateAgencyFlexibilityConfigProps) => {
  const { t } = useTranslation();

  const allowAgencyModification = useWatch({
    control,
    name: 'allowAgencyModification',
  });

  const FlexibilityRow = ({
    name,
    label,
    indented = false,
  }: Omit<FlexibilityRowProps, 'control'>) => (
    <div
      className={`flex items-center justify-between gap-6 w-full ${indented ? 'pl-4' : ''}`}
    >
      <Label className="text-sm text-gray-700 flex-1 min-w-0">{label}</Label>
      <YesNoStyledRadioGroup control={control} name={name} inline />
    </div>
  );

  return (
    <div className="flex flex-col gap-3 w-full">
      <FlexibilityRow
        name="allowAgencyModification"
        label={t('estimates.agencyFlexibility.mainQuestion')}
      />

      {allowAgencyModification === 'true' && (
        <div className="flex flex-col gap-2">
          <Label className="text-sm font-medium text-gray-700">
            {t('estimates.agencyFlexibility.agencyCan')}
          </Label>

          <FlexibilityRow
            name="showQuoteName"
            label={t('estimates.agencyFlexibility.showQuoteName')}
          />

          <Label className="text-sm font-medium text-gray-700">
            {t('estimates.agencyFlexibility.showParagraphs')}
          </Label>

          <FlexibilityRow
            name="showSummaryAndImportantPoints"
            label={t(
              'estimates.agencyFlexibility.showSummaryAndImportantPoints',
            )}
            indented
          />
          <FlexibilityRow
            name="showPriceIncludesExcludes"
            label={t('estimates.agencyFlexibility.showPriceIncludesExcludes')}
            indented
          />
          <FlexibilityRow
            name="showModificationCancellationConditions"
            label={t(
              'estimates.agencyFlexibility.showModificationCancellationConditions',
            )}
            indented
          />

          <FlexibilityRow
            name="showDates"
            label={t('estimates.agencyFlexibility.showDates')}
          />

          <FlexibilityRow
            name="allowAgencyInternationalFlights"
            label={t(
              'estimates.agencyFlexibility.allowAgencyInternationalFlights',
            )}
          />
          <FlexibilityRow
            name="allowAgencyMarginRate"
            label={t('estimates.agencyFlexibility.allowAgencyMarginRate')}
          />
          <FlexibilityRow
            name="allowAgencyAdditionalMargin"
            label={t('estimates.agencyFlexibility.allowAgencyAdditionalMargin')}
          />
        </div>
      )}
    </div>
  );
};

export default EstimateAgencyFlexibilityConfig;
